import { useEffect, useRef } from 'react';

export default function AmbientBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    // Respect reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId;
    let width = (canvas.width = window.innerWidth);
    let height = (canvas.height = window.innerHeight);

    // Track mouse coordinates with silky lerp smoothing
    const mouse = { x: width * 0.5, y: height * 0.3, targetX: width * 0.5, targetY: height * 0.3 };

    const handleResize = () => {
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
      initParticles();
    };

    const handleMouseMove = (e) => {
      mouse.targetX = e.clientX;
      mouse.targetY = e.clientY;
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('mousemove', handleMouseMove, { passive: true });

    // Node count adapted to screen resolution
    const particleCount = width < 768 ? 16 : 32;
    let particles = [];

    class Particle {
      constructor() {
        this.x = Math.random() * width;
        this.y = Math.random() * height;
        // Very slow, tranquil drift speed
        this.vx = (Math.random() - 0.5) * 0.35;
        this.vy = (Math.random() - 0.5) * 0.35;
        this.radius = Math.random() * 2 + 1.2;
        // Warm palette colors: peach, terracotta, subtle plum
        const colors = [
          'rgba(232, 149, 109, 0.45)', // warm terracotta
          'rgba(247, 190, 161, 0.55)', // soft peach
          'rgba(45, 23, 83, 0.22)',   // midnight plum
          'rgba(58, 112, 80, 0.35)',   // biosignal sage
        ];
        this.color = colors[Math.floor(Math.random() * colors.length)];
        this.pulse = Math.random() * Math.PI * 2;
        this.pulseSpeed = 0.015 + Math.random() * 0.02;
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;

        // Wrap around boundaries gently
        if (this.x < -20) this.x = width + 20;
        if (this.x > width + 20) this.x = -20;
        if (this.y < -20) this.y = height + 20;
        if (this.y > height + 20) this.y = -20;

        // Subtle organic breathing of particle radii
        this.pulse += this.pulseSpeed;

        // Gentle interactive mouse deflection
        const dx = mouse.x - this.x;
        const dy = mouse.y - this.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const maxDist = 180;
        if (dist < maxDist && dist > 0) {
          const force = (1 - dist / maxDist) * 0.02;
          this.x -= (dx / dist) * force * 15;
          this.y -= (dy / dist) * force * 15;
        }
      }

      draw() {
        const currentRadius = this.radius * (1 + 0.25 * Math.sin(this.pulse));
        ctx.beginPath();
        ctx.arc(this.x, this.y, currentRadius, 0, Math.PI * 2);
        ctx.fillStyle = this.color;
        ctx.fill();
      }
    }

    const initParticles = () => {
      particles = [];
      for (let i = 0; i < particleCount; i++) {
        particles.push(new Particle());
      }
    };

    initParticles();

    // Pulse wave simulation: occasional gentle biosignal ring expanding outward
    let signalWaves = [];
    let waveTimer = 0;

    const render = () => {
      // Smooth mouse lerping
      mouse.x += (mouse.targetX - mouse.x) * 0.05;
      mouse.y += (mouse.targetY - mouse.y) * 0.05;

      ctx.clearRect(0, 0, width, height);

      // Periodically trigger a tranquil biosignal pulse wave (every ~6 seconds)
      waveTimer++;
      if (waveTimer > 360) {
        waveTimer = 0;
        const origin = particles[Math.floor(Math.random() * particles.length)];
        if (origin) {
          signalWaves.push({
            x: origin.x,
            y: origin.y,
            radius: 5,
            maxRadius: 180,
            opacity: 0.18,
          });
        }
      }

      // Draw and update active signal waves
      for (let i = signalWaves.length - 1; i >= 0; i--) {
        const wave = signalWaves[i];
        wave.radius += 0.85;
        wave.opacity *= 0.985;

        ctx.beginPath();
        ctx.arc(wave.x, wave.y, wave.radius, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(232, 149, 109, ${wave.opacity})`;
        ctx.lineWidth = 1;
        ctx.stroke();

        if (wave.radius >= wave.maxRadius || wave.opacity <= 0.01) {
          signalWaves.splice(i, 1);
        }
      }

      // Draw connective synaptic threads between nearby nodes
      const maxConnectDist = width < 768 ? 100 : 140;
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < maxConnectDist) {
            const alpha = (1 - dist / maxConnectDist) * 0.14;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(232, 149, 109, ${alpha})`;
            ctx.lineWidth = 0.85;
            ctx.stroke();
          }
        }
      }

      // Update and draw particles
      particles.forEach((p) => {
        p.update();
        p.draw();
      });

      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 0,
        overflow: 'hidden',
      }}
      aria-hidden="true"
    >
      {/* 
        Layer 1: Breathing Organic Aurora Orbs
        These large diffused blurred radial blooms float slowly and breathe,
        giving an atmospheric cinematic filmic warmth to the page without clutter.
      */}
      <style>{`
        @keyframes float-aurora-1 {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(45px, -35px) scale(1.12); }
          66% { transform: translate(-30px, 25px) scale(0.94); }
          100% { transform: translate(0px, 0px) scale(1); }
        }

        @keyframes float-aurora-2 {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(-40px, 45px) scale(0.92); }
          66% { transform: translate(35px, -20px) scale(1.1); }
          100% { transform: translate(0px, 0px) scale(1); }
        }

        @keyframes float-aurora-3 {
          0% { transform: translate(0px, 0px) scale(1); }
          50% { transform: translate(25px, -40px) scale(1.15); }
          100% { transform: translate(0px, 0px) scale(1); }
        }

        .aurora-orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(85px);
          opacity: 0.65;
          will-change: transform;
        }
      `}</style>

      {/* Orb 1: Upper Right Soft Peach / Rose */}
      <div
        className="aurora-orb"
        style={{
          top: '-15%',
          right: '-10%',
          width: '55vw',
          height: '55vw',
          maxWidth: '750px',
          maxHeight: '750px',
          background: 'radial-gradient(circle, rgba(247, 190, 161, 0.42) 0%, rgba(232, 149, 109, 0.12) 50%, transparent 75%)',
          animation: 'float-aurora-1 26s ease-in-out infinite',
        }}
      />

      {/* Orb 2: Mid Left Terracotta Amber */}
      <div
        className="aurora-orb"
        style={{
          top: '35%',
          left: '-15%',
          width: '50vw',
          height: '50vw',
          maxWidth: '650px',
          maxHeight: '650px',
          background: 'radial-gradient(circle, rgba(232, 149, 109, 0.28) 0%, rgba(247, 190, 161, 0.08) 55%, transparent 75%)',
          animation: 'float-aurora-2 32s ease-in-out infinite',
        }}
      />

      {/* Orb 3: Bottom Right Subtle Royal Plum Veil */}
      <div
        className="aurora-orb"
        style={{
          bottom: '-20%',
          right: '15%',
          width: '52vw',
          height: '52vw',
          maxWidth: '700px',
          maxHeight: '700px',
          background: 'radial-gradient(circle, rgba(45, 23, 83, 0.1) 0%, rgba(232, 149, 109, 0.05) 50%, transparent 75%)',
          animation: 'float-aurora-3 28s ease-in-out infinite',
        }}
      />

      {/* Layer 2: Canvas with delicate synaptic nodes & signal ripples */}
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
        }}
      />
    </div>
  );
}
